<template>
   <BaseButton color="primary-500" class="w-full text-center" @click="changeTherapistFlow">
      <span class="w-full">
         {{ t('header.selectTherapist') }}
      </span>
   </BaseButton>
   <BaseDialog size="sm" v-model:open-dialog="changeTherapistModal">
      <template #header></template>
      <div class="grid grid-cols-12">
         <div class="col-span-12 flex gap-3 mb-6">
            {{ t('therapistProfile.changeTherapistTitle') }}
            <XMarkIcon
               @click="changeTherapistModal = false"
               class="h-12 w-12 cursor-pointer"
            ></XMarkIcon>
         </div>
         <div class="col-span-12 gap-5 flex justify-between">
            <BaseButton @click="redirect(false)" block variant="outlined" color="primary-500">
               {{ t('No') }}
            </BaseButton>
            <BaseButton @click="redirect(true)" block color="primary-500">
               {{ t('Yes') }}
            </BaseButton>
         </div>
      </div>
   </BaseDialog>
   <BaseDialog size="md" :no-close="true" v-model:open-dialog="openPrevTherapistSelection">
      <template #header></template>
      <div class="text-center">
         <div class="text-2xl mt-6 font-medium">Match with a Therapist</div>
         <div class="my-2 text-sm text-gray-600">
            Your previous therapist {{ therapistStatus?.previous_therapist?.name }} is unassigned
            due in activity
         </div>
         <div class="my-6">
            <BaseAvatar
               classes="h-10 w-10 md:h-20 md:w-20 mx-auto rounded-full mb-4"
               :image-id="therapistStatus?.previous_therapist?.image"
            />
         </div>
         <div class="text-gray-600 text-sm mb-6">
            You can rematch with your previous therapist or get matched to new therapist
         </div>
         <div
            v-if="rematchError"
            class="flex gap-2 mb-4 rounded bg-primary-50 border p-2 text-left border-primary-300"
         >
            <div>
               <InformationCircleIcon class="text-primary-400 h-6 w-6" />
            </div>
            <div class="text-primary-400 text-sm">
               {{ t('matchTherapistErrorTitle') }}.
               <span class="text-black text-sm"> {{ t('matchTherapistErrorSubtitle') }}</span>
            </div>
         </div>
         <div class="flex md:flex-row flex-col gap-2 justify-evenly mb-2 items-center">
            <BaseButton
               :loading="loadingState"
               @click="matchNewTherapistFlow"
               :variant="rematchError ? 'solid' : 'outlined'"
               color="primary-500"
            >
               Match with a new therapist
            </BaseButton>
            <BaseButton
               v-if="!rematchError"
               :loading="loadingState"
               @click="ChangeTherapist"
               color="primary-500"
            >
               Rematch with previous therapist
            </BaseButton>
         </div>
      </div>
   </BaseDialog>
</template>

<script setup lang="ts">
   import { InformationCircleIcon } from '@heroicons/vue/24/outline';
   import { XMarkIcon } from '@heroicons/vue/20/solid';
   const { selectTherapistRecommended } = usePlanStore();
   const { setTherapistReassign } = useClientInfo();
   import { storeToRefs } from 'pinia';
   const changeTherapistModal = ref(false);
   const openPrevTherapistSelection = ref(false);
   const { track } = useSegment();
   const { t } = useI18n();

   const props = defineProps<{
      isTherapist?: boolean;
      name: string;
   }>();
   const emits = defineEmits(['success']);

   const { therapistStatus, clientInformation } = storeToRefs(useClientInfo());
   const redirect = (action: boolean) => {
      useClientInfo().setRedirectionUrl(true);
      if (action) {
         track('Change Therapist Completed', {
            current_therapist_id: clientInformation?.value?.therapist?.id,
            retake_assessment: true,
         });
         navigateTo('/auth/client/register/therapy-reasons');
      } else {
         track('Change Therapist Completed', {
            current_therapist_id: clientInformation?.value?.therapist?.id,
            retake_assessment: false,
         });
         navigateTo('/therapist-recommendations?redirect=profile');
      }
   };
   const changeTherapistFlow = () => {
      if (
         therapistStatus?.value?.was_unassigned &&
         therapistStatus?.value?.previous_therapist?.id &&
         therapistStatus?.value?.previous_therapist?.active !== false
      ) {
         openPrevTherapistSelection.value = true;
      } else {
         changeTherapistModal.value = true;
      }
   };
   const matchNewTherapistFlow = () => {
      openPrevTherapistSelection.value = false;
      track('Change Therapist Started', {
         current_therapist_id: clientInformation?.value?.therapist?.id,
      });
      changeTherapistModal.value = true;
   };
   const loadingState = ref(false);
   const rematchError = ref(null);
   const ChangeTherapist = async () => {
      loadingState.value = true;
      rematchError.value = null;
      let { data, error } = await selectTherapistRecommended(
         therapistStatus?.value?.previous_therapist?.id,
      );
      if (data?.value) {
         emits('success');
         await useClientInfo().getTherapistStatus();
         await useClientInfo().getSelectedTherapistInfo();
         setTherapistReassign(true);
         openPrevTherapistSelection.value = false;
         loadingState.value = false;
      }
      if (error?.value) {
         rematchError.value = error?.value?.data?.message;
         loadingState.value = false;
      }
   };
</script>
